import { EyeOutlined } from "@ant-design/icons"
import { Button, Flex, Form, Skeleton, Space, Typography } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"
import { useNavigate } from "react-router-dom"

import { FixedContainer } from "../../../components/FixedContainer.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useDateFormat } from "../../../hooks/use-date-format.ts"
import { showErrorNotifications } from "../../../utils/handlers/handle-error"
import { showDocumentValidationError } from "../helpers/show-document-validation-error.tsx"
import {
  fetchProposalDocumentById,
  saveProposalDocument,
  updateProposalDocument
} from "../redux/thunks/document-proposal.thunks.ts"
import { fetchDocumentPreview } from "../redux/thunks/document-templates.thunks.ts"
import {
  type Document,
  type DocumentRequestData
} from "../types/document.types.ts"
import { type TemplateDocumentFormData } from "../types/template-document-form-data.tsx"
import { DocumentBackToLeadBtn } from "./components/DocumentBackToLeadBtn.tsx"
import { TemplateDocumentForm } from "./components/TemplateDocumentForm/TemplateDocumentForm.tsx"
import { TemplateDocumentPreview } from "./components/TemplateDocumentPreview.tsx"

const { Title } = Typography

function DocumentProposalEditPage({
  fetchDocumentById,
  updateDocument,
  saveDocument,
  fetchDocumentPreview
}: ConnectedProps<typeof connector>) {
  const navigate = useNavigate()
  const documentId = useParams<{ id: string }>()?.id as string
  const [form] = Form.useForm<TemplateDocumentFormData>()
  const { normalizeDate } = useDateFormat()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [document, setDocument] = useState<Document | null>(null)

  const leadId = document?.lead_id
  const alreadyHasFile = !!document?.attachments?.length

  const handleFetchDocumentPreview = useCallback(
    async (data: DocumentRequestData) => {
      return fetchDocumentPreview({ ...data, proposal_id: documentId }).unwrap()
    },
    [documentId, fetchDocumentPreview]
  )

  const handleUpdateDocument = useCallback(async () => {
    const data = await form.validateFields()
    return updateDocument({
      id: documentId,
      data: {
        ...data,
        completion_date: normalizeDate(data?.completion_date),
        start_date: normalizeDate(data?.start_date)
      }
    })?.unwrap()
  }, [documentId, form, updateDocument])

  const handlePreviewUpdateDocument = useCallback(async () => {
    try {
      setSaving(true)

      const res = await handleUpdateDocument()
      const updatedLeadId = res?.lead_id

      if (!updatedLeadId) throw new Error("Lead id is required")

      navigate(RoutesPaths.lead.view(updatedLeadId))
    } catch (e) {
      if (!showDocumentValidationError(form, e)) showErrorNotifications(e)
    } finally {
      setSaving(false)
    }
  }, [navigate, handleUpdateDocument])

  const handleSaveAndUpdate = useCallback(async () => {
    try {
      setSaving(true)
      await handleUpdateDocument()

      if (!documentId) throw new Error("Document id is required")

      await saveDocument(documentId).unwrap()
      const leadId = document?.lead_id
      if (!leadId) throw new Error("Lead id is required")
      navigate(RoutesPaths.lead.view(leadId))
    } catch (e) {
      if (!showDocumentValidationError(form, e)) showErrorNotifications(e)
    } finally {
      setSaving(false)
    }
  }, [
    document?.lead_id,
    documentId,
    handlePreviewUpdateDocument,
    navigate,
    saveDocument
  ])

  const handleGetDocument = useCallback(async () => {
    if (!documentId) return

    try {
      setLoading(true)
      const document = await fetchDocumentById(documentId)?.unwrap()
      if (document) setDocument(document)
    } finally {
      setLoading(false)
    }
  }, [documentId, fetchDocumentById])

  useEffect(() => {
    void handleGetDocument()
    generateDocumentTitle("Edit Document")
  }, [handleGetDocument])

  if (loading) return <Skeleton.Node active className={"size-full"} />
  if (!loading && !document) return <div>Document not found</div>

  return (
    <FixedContainer>
      <Flex vertical gap={"small"} className={"overflow-hidden"}>
        <Flex justify={"space-between"} align={"center"} wrap gap={"small"}>
          <Flex vertical>
            <Title level={4}>Edit Document</Title>
            {/*<Text type="secondary">Template: {template?.name}</Text>*/}
          </Flex>
          <Space wrap>
            {leadId && (
              <DocumentBackToLeadBtn leadId={leadId} saving={saving} />
            )}
            <TemplateDocumentPreview
              saving={saving}
              form={form}
              leadId={leadId}
              customerId={document?.customer_id}
              alreadyHasFile={alreadyHasFile}
              fetchDocumentPreview={handleFetchDocumentPreview}
              onSubmitClick={handlePreviewUpdateDocument}
              onGenerateClick={handleSaveAndUpdate}
            />

            <Button
              loading={saving}
              onClick={form.submit}
              variant={"solid"}
              color={"primary"}
              icon={<EyeOutlined />}
            >
              {alreadyHasFile ? "Update & Regenerate" : "Update & Generate"}
            </Button>
          </Space>
        </Flex>

        <TemplateDocumentForm
          form={form}
          saving={saving}
          projectTypes={document?.project_type_details || []}
          initialValues={document || undefined}
          onSubmit={handleSaveAndUpdate}
        />
      </Flex>
    </FixedContainer>
  )
}

const mapStateToProps = () => ({})

const mapDispatchToProps = {
  fetchDocumentById: fetchProposalDocumentById,
  updateDocument: updateProposalDocument,
  saveDocument: saveProposalDocument,
  fetchDocumentPreview
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(DocumentProposalEditPage)
